import { Component, OnInit, Input } from '@angular/core';
import * as _ from 'lodash';
import { GfTitleService } from '../../../../services/core.title.service';
import { ProjectService } from '../../../../services/project.service';
import { SettingService } from '../../../../services/core.setting.service';
import { UiService } from '../../../../services/core.ui.service';
import { StateService } from '@uirouter/angular';

@Component({
  selector: 'project-admin-scm',
  template: require('./project.admin.scm.html')
})
export class ProjectAdminScmComponent implements OnInit {
  @Input() project: any;
  @Input() isProjectAdmin: any;

  isLoading = true;
  scmData: any = false;
  accessTypes = { ssh: true, https: false };
  associationTypes = { 'Ignore': 0, 'Allow': 2, 'Require': 1 };
  enableLFS = false;
  conventionalCommits: any = { enabled: false };
  forcePush: any = { siteAllow: 1 };
  codeSearch: any = { enabled: false, lastScmIndex: false };
  codeTagging: any = { allowChangeTagging: true, taggingEnabled: true };
  booleans = { 'Yes': true, 'No': false };
  YesNo = { 'Yes': '1', 'No': '0' };

  originalCodeSearch: any;
  originalConventionalCommits: any;
  originalScmOptions: any;
  scmAllowChangeScmAccess: boolean;
  originalCodeTagging: any;
  saving = false;

  constructor(
    private titleService: GfTitleService,
    private stateService: StateService,
    private projectService: ProjectService,
    private settingService: SettingService,
    private uiService: UiService
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.project.name, 'Admin', 'SCM Options');

    const unixName = this.stateService.params.unixName;
    const actions = {
      scmData: this.projectService.getScm(unixName),
      scmIndexEnabledSetting: this.settingService.fetch('project', this.project.id, 'scm.indexingEnabled', 0),
      lastScmIndexSetting: this.settingService.fetch('project', this.project.id, 'scm.lastIndexDate', '1970-01-01'),
      scmAllowChangeScmAccess: this.settingService.fetch('config', 0, 'scm.allowChangeScmAccess'),
      siteTaggingEnabled: this.settingService.fetch('config', 0, 'scm.taggingEnabled', 1),
      taggingEnabled: this.settingService.fetch('project', this.project.id, 'scm.taggingEnabled'),
      siteAllowChangeTagging: this.settingService.fetch('config', 0, 'scm.allowChangeTagging'),
      siteIndexingEnabled: this.settingService.fetch('config', 0, 'scm.indexingEnabled', 0),
      siteAllowChangeIndexing: this.settingService.fetch('config', 0, 'scm.allowChangeIndexing', 1),
      requireConventionalCommit: this.settingService.fetch('project', this.project.id, 'scm.git.requireConventionalCommit', 0),
      siteAllowChangeConventionalCommit: this.settingService.fetch('config', 0, 'scm.git.allowChangeConventionalCommit', 1),
      siteAllowChangeForcePush: this.settingService.fetch('config', 0, 'scm.git.allowChangeForcePush', 1)
    };

    Promise.all(Object.values(actions)).then(results => {
      const keys = Object.keys(actions);
      const result: any = {};
      keys.forEach((key, index) => result[key] = results[index]);

      this.scmData = _.get(result, 'scmData', false);
      this.codeSearch = {
        siteAllow: parseInt(_.get(result, 'siteAllowChangeIndexing.settingValue', '1')),
        siteEnabled: _.get(result, 'siteIndexingEnabled.settingValue', 0),
        enabled: _.get(result, 'scmIndexEnabledSetting.settingValue', 0) == 1,
        lastScmIndex: _.get(result, 'lastScmIndexSetting.settingValue', '1970-01-01')
      };
      if (this.codeSearch.lastScmIndex === '1970-01-01') {
        this.codeSearch.lastScmIndex = '(none)';
      }
      this.originalCodeSearch = _.clone(this.codeSearch);
      this.originalScmOptions = _.clone(this.scmData?.options);

      this.conventionalCommits = {
        siteAllow: parseInt(_.get(result, 'siteAllowChangeConventionalCommit.settingValue', '1')),
        enabled: _.get(result, 'requireConventionalCommit.settingValue', 0) == 1
      };
      this.originalConventionalCommits = _.clone(this.conventionalCommits);

      this.forcePush = {
        siteAllow: parseInt(_.get(result, 'siteAllowChangeForcePush.settingValue', '1'))
      };

      this.scmAllowChangeScmAccess = _.get(result, 'scmAllowChangeScmAccess.settingValue', 0) == '1';

      this.codeTagging = {
        allowChangeTagging: parseInt(_.get(result, 'siteAllowChangeTagging.settingValue', '1')) === 1,
        taggingEnabled: _.get(result, 'taggingEnabled.settingValue', '')
      };
      if (this.codeTagging.taggingEnabled === '') {
        this.codeTagging.taggingEnabled = _.get(result, 'siteTaggingEnabled.settingValue', '1') == 1;
      }
      this.originalCodeTagging = _.clone(this.codeTagging);
      this.isLoading = false;
    });
  }

  setOption(path: string, value: any): boolean {
    if (value === undefined || value === _.get(this, path)) return false;
    _.set(this, path, value);
    return true;
  }

  saveScmOptions(): void {
    this.saving = true;
    const unixName = this.stateService.params.unixName;
    const actions = [];

    if (!_.isEqual(this.originalScmOptions, this.scmData?.options)) {
      actions.push(this.projectService.postScm(unixName, this.scmData.options));
    }
    if (this.originalCodeSearch.enabled !== this.codeSearch.enabled) {
      actions.push(this.settingService.post({
        section: 'project',
        refId: this.project.id,
        settingName: 'scm.indexingEnabled',
        settingValue: this.codeSearch.enabled ? "1" : "0"
      }));
    }
    if (this.originalConventionalCommits.enabled !== this.conventionalCommits.enabled) {
      actions.push(this.settingService.post({
        section: 'project',
        refId: this.project.id,
        settingName: 'scm.git.requireConventionalCommit',
        settingValue: this.conventionalCommits.enabled ? "1" : "0"
      }));
    }
    if (this.codeTagging.allowChangeTagging == 1 &&
        this.originalCodeTagging.taggingEnabled != this.codeTagging.taggingEnabled) {
      const s = {
        section: 'project',
        refId: this.project.id,
        settingName: 'scm.taggingEnabled',
        settingValue: this.codeTagging.taggingEnabled ? "1" : "0"
      };
      actions.push(this.settingService.post(s));
    }
    if (actions.length > 0) {
      Promise.all(actions)
        .then(() => {
          this.uiService.message.showSuccess("SCM Options were updated");
        })
        .catch(() => {
          this.uiService.message.showWarning("Sorry, could not update the SCM Options");
        })
        .finally(() => {
          this.saving = false;
        });
    } else {
      this.saving = false;
    }
  }

  onForcePushChange(value: boolean): void {
    if (value) {
      ($('#force-push-confirm-modal') as any).modal('show');
      return;
    }
    this.setOption('scmData.options.allowForcePush', false);
  }

  confirmForcePush(): void {
    ($('#force-push-confirm-modal') as any).modal('hide');
    this.setOption('scmData.options.allowForcePush', true);
  }

  cancelForcePush(): void {
    ($('#force-push-confirm-modal') as any).modal('hide');
  }
}
